import { ComponentType, lazy, Suspense, ViewTransition } from "react";
import { BrowserRouter } from "router2";

import { useIsWeb } from "~/hook/base/useIsWeb.ts";
import {
  config,
  PAGE_PUSH_TRANSITION_CLASS,
  PAGE_PUSH_TRANSITION_TYPE,
  routes,
} from "~/service/router.ts";
import { BACKDROP_ANIMATION_DURATION } from "~/ui/base/Backdrop.tsx";
import { AuthProvider } from "~/ui/provider/Auth.tsx";
import { I18nProvider } from "~/ui/provider/I18nProvider.tsx";
import { LivePokeStreamProvider } from "~/ui/provider/LivePokeStreamProvider.tsx";
import { MessageProvider } from "~/ui/provider/Message.tsx";
import { NotificationProvider } from "~/ui/provider/Notification.tsx";
import { PwaProvider } from "~/ui/provider/PwaProvider.tsx";
import { ServerHealthProvider } from "~/ui/provider/ServerHealthProvider.tsx";
import { StackedLayerProvider } from "~/ui/provider/StackedLayerProvider.tsx";
import { ThemeProvider } from "~/ui/provider/ThemeProvider.tsx";

const Introduction = lazy(() =>
  import("~/ui/page/Introduction.tsx").then((mod) => ({
    default: mod.Introduction,
  })),
);

const AppContent = ({ Page }: { Page: ComponentType }) => {
  const isWebRoute = useIsWeb();

  return (
    <I18nProvider>
      <ThemeProvider>
        <ServerHealthProvider>
          <NotificationProvider>
            <PwaProvider
              fallback={
                <StackedLayerProvider
                  unmountAfter={BACKDROP_ANIMATION_DURATION}
                >
                  <Suspense>
                    <div className="mobile-column">
                      <Introduction />
                    </div>
                  </Suspense>
                </StackedLayerProvider>
              }
              skipFallback={isWebRoute}
            >
              {(prefetch) => (
                <AuthProvider myInfo={prefetch.myInfo}>
                  <MessageProvider>
                    <StackedLayerProvider
                      unmountAfter={BACKDROP_ANIMATION_DURATION}
                    >
                      <LivePokeStreamProvider>
                        <ViewTransition
                          default="none"
                          update={{
                            default: "none",
                            [PAGE_PUSH_TRANSITION_TYPE]:
                              PAGE_PUSH_TRANSITION_CLASS,
                          }}
                        >
                          {isWebRoute ? (
                            <Page />
                          ) : (
                            <div className="mobile-column">
                              <Page />
                            </div>
                          )}
                        </ViewTransition>
                      </LivePokeStreamProvider>
                    </StackedLayerProvider>
                  </MessageProvider>
                </AuthProvider>
              )}
            </PwaProvider>
          </NotificationProvider>
        </ServerHealthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
};

export const App = () => {
  return (
    <BrowserRouter config={config} routes={routes}>
      {(Page) => <AppContent Page={Page} />}
    </BrowserRouter>
  );
};
